import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BackButton } from '../components/back-button'
import { useQuery } from '@tanstack/react-query'
import {
  Spinner,
  apiClient,
  formatBytes,
  formatRelativeTime,
  getSupabase,
  useCurrentUser,
  cn,
} from '@dogdisk/shared'
import { MobileScreen } from '../components/mobile-screen'
import { ReportModal } from '../components/modals/report-modal'
import { fileOpenHref } from '../_lib/file-route'
import { resourceUrl } from '../_lib/resource-node-pref'

const HISTORY_KEY = 'search_history'

// B4-#11 zNyr2 公开搜索 三 tab
//
// - my_files:仅登录态,走 supabase REST file_info(原私有文件搜索保留)
// - public_packs:任何人,走 GET /gapi/public/discover/search?type=pack
// - public_users:任何人,走 GET /gapi/public/discover/search?type=user
// - public_categories:任何人,走 GET /gapi/public/discover/search?type=category

type SearchTab = 'my_files' | 'public_packs' | 'public_users' | 'public_categories'

type FileHit = {
  id: string
  name: string
  type: string
  size: number | null
  created_at: string
}

type PackHit = {
  pack_id: string
  name: string
  description: string
  access_mode: string
  price_coin: number
  like_count: number
  item_count: number
  integrator_username: string
  integrator_avatar: string
}

type UserHit = {
  user_id: string
  username: string
  avatar_url: string
  signature: string
}

type CategoryHit = {
  category_id: string
  name: string
  icon: string
  file_type: string
}

type SearchPublicResp<T> = {
  type: string
  query: string
  limit: number
  offset: number
  total: number
  items: T[]
}

function loadHistory(): string[] {
  if (typeof localStorage === 'undefined') return []
  try {
    const raw = localStorage.getItem(HISTORY_KEY)
    return raw ? (JSON.parse(raw) as string[]) : []
  } catch {
    return []
  }
}

function saveHistory(list: string[]) {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(list.slice(0, 8)))
  } catch {
    /* noop */
  }
}

export function SearchPage() {
  const navigate = useNavigate()
  const { status } = useCurrentUser()
  const isAuth = status === 'authenticated'
  const [query, setQuery] = useState('')
  const [active, setActive] = useState('')
  const [tab, setTab] = useState<SearchTab>(isAuth ? 'my_files' : 'public_packs')
  const [history, setHistory] = useState<string[]>([])

  useEffect(() => setHistory(loadHistory()), [])

  const submit = (q: string) => {
    const t = q.trim()
    if (t.length < 1) return
    setActive(t)
    const next = [t, ...history.filter((h) => h !== t)].slice(0, 8)
    setHistory(next)
    saveHistory(next)
  }

  const files = useQuery({
    queryKey: ['search', 'files', active] as const,
    enabled: isAuth && tab === 'my_files' && active.length >= 1,
    queryFn: async () => {
      const sb = getSupabase()
      // owner 收口走 v_file_info_mine(内建 user_id=auth.uid() + status='active');size_original 已平坦化。
      // 直查 file_info 会因 file_info_select_visibility 宽 policy(is_public 默认 true)漏入别人的公开文件。
      const { data, error } = await sb
        .from('v_file_info_mine')
        .select('id, name, type, created_at, size_original')
        .ilike('name', `%${active}%`)
        .order('created_at', { ascending: false })
        .limit(50)
      if (error) throw error
      type Row = Omit<FileHit, 'size'> & { size_original: number | null }
      return ((data ?? []) as unknown as Row[]).map(({ size_original, ...rest }) => ({
        ...rest,
        size: size_original ?? null,
      })) satisfies FileHit[]
    },
  })

  const packs = useQuery({
    queryKey: ['search', 'public_packs', active] as const,
    enabled: tab === 'public_packs' && active.length >= 1,
    queryFn: () =>
      apiClient.get<SearchPublicResp<PackHit>>('/gapi/public/discover/search', {
        query: { query: active, type: 'pack', limit: 20 },
      }),
  })

  const users = useQuery({
    queryKey: ['search', 'public_users', active] as const,
    enabled: tab === 'public_users' && active.length >= 1,
    queryFn: () =>
      apiClient.get<SearchPublicResp<UserHit>>('/gapi/public/discover/search', {
        query: { query: active, type: 'user', limit: 20 },
      }),
  })

  const categories = useQuery({
    queryKey: ['search', 'public_categories', active] as const,
    enabled: tab === 'public_categories' && active.length >= 1,
    queryFn: () =>
      apiClient.get<SearchPublicResp<CategoryHit>>('/gapi/public/discover/search', {
        query: { query: active, type: 'category', limit: 20 },
      }),
  })

  const TABS: { key: SearchTab; label: string; authOnly?: boolean }[] = [
    { key: 'my_files', label: '我的文件', authOnly: true },
    { key: 'public_packs', label: '资源包' },
    { key: 'public_users', label: '用户' },
    { key: 'public_categories', label: '分类' },
  ]

  const visibleTabs = TABS.filter((t) => !t.authOnly || isAuth)

  return (
    <MobileScreen>
      <header className="flex items-center gap-3 px-6 pt-[44px] pb-3">
        <BackButton className="shrink-0" />
        <form
          onSubmit={(e) => {
            e.preventDefault()
            submit(query)
          }}
          className="flex flex-1 items-center gap-2 rounded-lg bg-[#F6F7F8] px-3"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="搜索文件 / 资源包 / 用户 / 分类"
            autoFocus
            className="h-9 flex-1 bg-transparent text-[14px] outline-none placeholder:text-[#9CA3AF]"
          />
        </form>
        <button type="button" onClick={() => navigate(-1)} className="text-[14px] text-[#19caad]">
          取消
        </button>
      </header>

      <nav className="flex gap-2 overflow-x-auto px-6 pb-2">
        {visibleTabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={cn(
              'shrink-0 rounded-full px-3 py-1 text-[12px] font-medium',
              tab === t.key
                ? 'bg-[#19caad] text-white'
                : 'bg-[#F6F7F8] text-[#9CA3AF]',
            )}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <main
        data-testid="k9NLf"
        data-frame-id={tab === 'my_files' ? 'k9NLf' : 'zNyr2'}
        className="flex flex-1 flex-col gap-4 px-6 pt-1 pb-12"
      >
        {!active && history.length > 0 && (
          <section>
            <div className="flex items-center justify-between pb-2">
              <p className="text-[13px] font-semibold text-[#1A1A1A]">搜索历史</p>
              <button
                type="button"
                onClick={() => {
                  setHistory([])
                  saveHistory([])
                }}
                className="text-[12px] text-[#9CA3AF]"
              >
                清空
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {history.map((h) => (
                <button
                  key={h}
                  type="button"
                  onClick={() => {
                    setQuery(h)
                    submit(h)
                  }}
                  className="rounded-full bg-[#F6F7F8] px-3 py-1 text-[12px] text-[#1A1A1A]"
                >
                  {h}
                </button>
              ))}
            </div>
          </section>
        )}

        {active && tab === 'my_files' && (
          <FileResults navigate={navigate} loading={files.isLoading} items={files.data} />
        )}
        {active && tab === 'public_packs' && (
          <PackResults loading={packs.isLoading} items={packs.data?.items} />
        )}
        {active && tab === 'public_users' && (
          <UserResults loading={users.isLoading} items={users.data?.items} />
        )}
        {active && tab === 'public_categories' && (
          <CategoryResults loading={categories.isLoading} items={categories.data?.items} />
        )}
      </main>
    </MobileScreen>
  )
}

function FileResults({
  navigate,
  loading,
  items,
}: {
  navigate: ReturnType<typeof useNavigate>
  loading: boolean
  items: FileHit[] | undefined
}) {
  return (
    <section>
      <div className="flex items-center justify-between pb-2">
        <p className="text-[13px] font-semibold text-[#1A1A1A]">我的文件</p>
        <span className="text-[12px] text-[#9CA3AF]">{items?.length ?? 0} 个结果</span>
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><Spinner className="text-gray-400" /></div>
      ) : items && items.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {items.map((f) => (
            <li key={f.id} className="flex items-center gap-3 rounded-xl bg-[#F6F7F8] px-3 py-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-white text-[14px]">
                {f.type === 'folder' ? '📁' : '📄'}
              </span>
              <button
                type="button"
                onClick={() => navigate(fileOpenHref(f))}
                className="flex min-w-0 flex-1 flex-col text-left"
              >
                <span className="truncate text-[14px] font-medium text-[#1A1A1A]">{f.name}</span>
                <span className="text-[11px] text-[#9CA3AF]">
                  {f.type === 'folder'
                    ? `文件夹 · ${formatRelativeTime(f.created_at)}`
                    : `${f.size ? formatBytes(f.size) : ''} · ${f.created_at.slice(0, 10)}`}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="py-8 text-center text-[13px] text-[#9CA3AF]">无匹配结果</p>
      )}
    </section>
  )
}

function PackResults({ loading, items }: { loading: boolean; items: PackHit[] | undefined }) {
  return (
    <section>
      <div className="flex items-center justify-between pb-2">
        <p className="text-[13px] font-semibold text-[#1A1A1A]">资源包</p>
        <span className="text-[12px] text-[#9CA3AF]">{items?.length ?? 0} 个结果</span>
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><Spinner className="text-gray-400" /></div>
      ) : items && items.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {items.map((p) => (
            <li key={p.pack_id} className="rounded-xl bg-[#F6F7F8] px-3 py-3">
              <a
                href={`#/pack/${p.pack_id}`}
                className="flex flex-col gap-1.5 text-left"
              >
                <p className="truncate text-[14px] font-semibold text-[#1A1A1A]">{p.name}</p>
                {p.description && (
                  <p className="line-clamp-2 text-[12px] text-[#9CA3AF]">{p.description}</p>
                )}
                <div className="flex items-center gap-2 text-[11px] text-[#9CA3AF]">
                  <span>📦 {p.item_count} 项</span>
                  <span>·</span>
                  <span>❤ {p.like_count}</span>
                  <span>·</span>
                  <span>
                    {p.access_mode === 'free'
                      ? '免费'
                      : p.access_mode === 'paid'
                        ? `${p.price_coin.toLocaleString()} 金币`
                        : '加密'}
                  </span>
                </div>
                {p.integrator_username && (
                  <p className="text-[11px] text-[#9CA3AF]">@{p.integrator_username}</p>
                )}
              </a>
            </li>
          ))}
        </ul>
      ) : (
        <p className="py-8 text-center text-[13px] text-[#9CA3AF]">无匹配资源包</p>
      )}
    </section>
  )
}

function UserResults({ loading, items }: { loading: boolean; items: UserHit[] | undefined }) {
  // B3-#6 DY9nl 举报弹框入口:用户搜索结果上的"举报"按钮
  const [reportTarget, setReportTarget] = useState<UserHit | null>(null)
  return (
    <section>
      <div className="flex items-center justify-between pb-2">
        <p className="text-[13px] font-semibold text-[#1A1A1A]">用户</p>
        <span className="text-[12px] text-[#9CA3AF]">{items?.length ?? 0} 个结果</span>
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><Spinner className="text-gray-400" /></div>
      ) : items && items.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {items.map((u) => (
            <li key={u.user_id} className="flex items-center gap-3 rounded-xl bg-[#F6F7F8] px-3 py-3">
              {u.avatar_url ? (
                <img src={resourceUrl(u.avatar_url)} alt={u.username} className="h-10 w-10 rounded-full object-cover" />
              ) : (
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#19caad] text-[13px] font-semibold text-white">
                  {u.username.slice(0, 1).toUpperCase()}
                </span>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-[14px] font-medium text-[#1A1A1A]">@{u.username}</p>
                {u.signature && (
                  <p className="truncate text-[11px] text-[#9CA3AF]">{u.signature}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setReportTarget(u)}
                className="shrink-0 rounded-md bg-white px-2 py-1 text-[11px] text-[#FF6B6B]"
                aria-label="举报"
              >
                举报
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="py-8 text-center text-[13px] text-[#9CA3AF]">无匹配用户</p>
      )}
      {reportTarget && (
        <ReportModal
          open={!!reportTarget}
          targetUserId={reportTarget.user_id}
          targetUsername={reportTarget.username}
          onClose={() => setReportTarget(null)}
        />
      )}
    </section>
  )
}

function CategoryResults({
  loading,
  items,
}: {
  loading: boolean
  items: CategoryHit[] | undefined
}) {
  return (
    <section>
      <div className="flex items-center justify-between pb-2">
        <p className="text-[13px] font-semibold text-[#1A1A1A]">分类</p>
        <span className="text-[12px] text-[#9CA3AF]">{items?.length ?? 0} 个结果</span>
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><Spinner className="text-gray-400" /></div>
      ) : items && items.length > 0 ? (
        <ul className="grid grid-cols-3 gap-2">
          {items.map((c) => (
            <li
              key={c.category_id}
              className="flex flex-col items-center gap-1 rounded-xl bg-[#F6F7F8] px-2 py-3 text-center"
            >
              <span className="text-[20px]">{c.icon || '📁'}</span>
              <p className="line-clamp-1 text-[12px] font-medium text-[#1A1A1A]">{c.name}</p>
              {c.file_type && (
                <p className="text-[10px] text-[#9CA3AF]">{c.file_type}</p>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="py-8 text-center text-[13px] text-[#9CA3AF]">无匹配分类</p>
      )}
    </section>
  )
}
